import * as R from 'ramda';
import adapter from 'dataModule/adapter';
import { httpGet, httpPost, httpPut, httpDelete, httpPatch } from 'apiModule/rest';
import { getQueriesString, getIdsObject } from 'utilsModule';

const createQuestionnaire = async ({ content }) =>
  R.pipe(adapter.questionnaires.createQuestionnaire.fe2be, httpPost('questionnaire/template'), (response) =>
    response.then(adapter.questionnaires.createQuestionnaire.be2fe),
  )(content);

// For id on parameter
const updateQuestionnaire = async ({ content, params: { ids } }) => {
  const url = R.pipe(getIdsObject, ({ id }) => `questionnaire/template/${id}`)(ids);

  return R.pipe(adapter.questionnaires.updateQuestionnaire.fe2be, httpPut(url), (response) =>
    response.then(adapter.questionnaires.updateQuestionnaire.be2fe),
  )(content);
};

const retrieveQuestionnaire = async ({ params: { ids } }) =>
  R.pipe(
    getIdsObject,
    ({ questionnaireId }) => httpGet(`questionnaire/template/${questionnaireId}`),
    (response) => response.then(adapter.questionnaires.retrieveQuestionnaire.be2fe),
  )(ids);

const retrieveQuestionnaires = async ({ params: { queries } }) =>
  R.pipe(
    getQueriesString,
    (queriesString) => httpGet(`questionnaire/template${queriesString}`),
    (response) => response.then(adapter.questionnaires.retrieveQuestionnaires.be2fe),
  )(queries);

const fetchAvailableQuestionnaireTemplates = async () =>
  R.pipe(
    () => httpGet('questionnaire/enroll/available'),
    (response) => response.then(adapter.questionnaires.retrieveAvailableQuestionnaireTemplates.be2fe),
  )();

const deleteQuestionnaireTemplate = async ({ params: { ids } }) =>
  R.pipe(
    getIdsObject,
    ({ questionnaireTemplateId }) => httpDelete(`questionnaire/template/${questionnaireTemplateId}`),
    (response) => response.then(adapter.questionnaires.deleteQuestionnaireTemplate.be2fe),
  )(ids);

const retrieveEnrolledQuestionnaires = async ({ params: { queries } }) =>
  R.pipe(
    getQueriesString,
    (queriesString) => httpGet(`questionnaire/enroll${queriesString}`),
    (response) => response.then(adapter.questionnaires.retrieveEnrolledQuestionnaires.be2fe),
  )(queries);

const retrieveStandaloneQuestionnaireTasks = async ({ params: { queries } }) =>
  R.pipe(
    getQueriesString,
    (queriesString) => httpGet(`tasks/questionnaire${queriesString}`),
    (response) => response.then(adapter.questionnaires.retrieveStandaloneQuestionnaireTasks.be2fe),
  )(queries);

const enrollQuestionnaire = async ({ content }) =>
  R.pipe(adapter.questionnaires.enrollQuestionnaire.fe2be, httpPost('questionnaire/enroll'), (response) =>
    response.then(adapter.questionnaires.enrollQuestionnaire.be2fe),
  )(content);

const updateQuestionnaireEnrollment = async ({ params: { ids }, content }) =>
  R.pipe(
    adapter.questionnaires.updateQuestionnaireEnrollment.fe2be,
    R.pipe(getIdsObject, ({ questionnaireEnrollmentId }) =>
      httpPatch(`questionnaire/enroll/${questionnaireEnrollmentId}`),
    )(ids),
    (response) => response.then(adapter.questionnaires.updateQuestionnaireEnrollment.be2fe),
  )(content);

const dischargeQuestionnaireEnrollment = async ({ params: { ids }, content }) =>
  R.pipe(
    adapter.questionnaires.dischargeQuestionnaireEnrollment.fe2be,
    R.pipe(getIdsObject, ({ questionnaireEnrollmentId }) =>
      httpPatch(`questionnaire/enroll/${questionnaireEnrollmentId}/discharge`),
    )(ids),
    (response) => response.then(adapter.questionnaires.dischargeQuestionnaireEnrollment.be2fe),
  )(content);

const retrieveQuestionnaireEnrollment = async ({ params: { ids } }) =>
  R.pipe(
    getIdsObject,
    ({ questionnaireEnrollmentId }) => httpGet(`questionnaire/enroll/${questionnaireEnrollmentId}`),
    (response) => response.then(adapter.questionnaires.retrieveQuestionnaireEnrollment.be2fe),
  )(ids);

const retrieveQuestionnaireResults = async ({ params: { queries } }) =>
  R.pipe(
    getQueriesString,
    (queriesString) => httpGet(`questionnaire/response${queriesString}`),
    (response) => response.then(adapter.questionnaires.retrieveQuestionnaireResults.be2fe),
  )(queries);

const retrieveQuestionnaireResponse = async ({ params: { ids } }) =>
  R.pipe(
    getIdsObject,
    ({ responseId }) => httpGet(`questionnaire/response/${responseId}`),
    (response) => response.then(adapter.questionnaires.retrieveQuestionnaireResponse.be2fe),
  )(ids);

const createQuestionnaireNote = async ({ content }) =>
  R.pipe(adapter.questionnaires.createQuestionnaireNote.fe2be, httpPost('questionnaire/note'), (response) =>
    response.then(adapter.questionnaires.createQuestionnaireNote.be2fe),
  )(content);

const retrieveQuestionnaireNotes = async ({ params: { queries } }) =>
  R.pipe(
    getQueriesString,
    (queriesString) => httpGet(`questionnaire/note${queriesString}`),
    (response) => response.then(adapter.questionnaires.retrieveQuestionnaireNotes.be2fe),
  )(queries);

const markQuestionnaireNoteAsError = async ({ content }) =>
  R.pipe(
    adapter.questionnaires.markQuestionnaireNoteAsError.fe2be,
    httpPost('questionnaire/note/markNoteAsError'),
    (response) => response.then(adapter.questionnaires.markQuestionnaireNoteAsError.be2fe),
  )(content);

const DM = {
  createQuestionnaire,
  updateQuestionnaire,
  retrieveQuestionnaire,
  retrieveQuestionnaires,
  fetchAvailableQuestionnaireTemplates,
  deleteQuestionnaireTemplate,
  retrieveEnrolledQuestionnaires,
  retrieveStandaloneQuestionnaireTasks,
  enrollQuestionnaire,
  updateQuestionnaireEnrollment,
  retrieveQuestionnaireEnrollment,
  retrieveQuestionnaireResults,
  dischargeQuestionnaireEnrollment,
  retrieveQuestionnaireResponse,
  createQuestionnaireNote,
  retrieveQuestionnaireNotes,
  markQuestionnaireNoteAsError,
};

const ajax = async ({ method, input }) => ({ [method]: await DM[method](input) });

export default { ...DM, ajax };
